import React from 'react';
import styled from 'styled-components';
import {Map, Placemark, Polygon} from 'react-yandex-maps';

import YandexMapsProvider, {hasYandexMapsApiKey} from '../common/YandexMaps';
import {
  createRentalReturnZone,
  deleteRentalReturnZone,
  getRentalLocations,
  getRentalReturnZones,
  getRentalVehicles,
  updateRentalReturnZone,
} from '../../api/rental';
import Layout from './Layout';
import {
  ActionButton,
  Actions,
  Badge,
  Card,
  CardTitle,
  DefinitionList,
  ErrorText,
  Input,
  List,
  ListButton,
  Select,
  State,
  SuccessText,
} from './styles';

const statusColors = {
  available: '#21c17a',
  reservation_pending: '#e5b13c',
  reserved: '#1a9ee8',
  rented: '#7b61ff',
  inspection: '#e58a3c',
  maintenance: '#e05c5c',
};

const statusLabels = {
  available: 'Доступен',
  reservation_pending: 'Ожидает оплаты',
  reserved: 'Забронирован',
  rented: 'В аренде',
  inspection: 'На осмотре',
  maintenance: 'Обслуживание',
};

const DEFAULT_CENTER = [44.952117, 34.102417];
const DEFAULT_ZOOM = 11;
const FOCUS_ZOOM = 14;

const money = kopecks => `${(Number(kopecks || 0) / 100).toLocaleString('ru-RU')} ₽/сутки`;

const formatPoint = point => (
  point && point.lat !== null && point.lat !== undefined
    ? `${Number(point.lat).toFixed(6)}, ${Number(point.lng).toFixed(6)}`
    : 'не заданы'
);

/**
 * Центр зоны — среднее по вершинам: достаточно, чтобы перевести карту
 * к выбранному контуру, и не тянет за собой геометрию из API карт.
 */
const zoneCenter = zone => {
  const points = (zone && zone.polygon) || [];
  if (!points.length) { return null; }
  const sum = points.reduce((total, point) => ({
    lat: total.lat + Number(point.lat),
    lng: total.lng + Number(point.lng),
  }), {lat: 0, lng: 0});
  return {lat: sum.lat / points.length, lng: sum.lng / points.length};
};

function FleetMapContent({authorization}) {
  const [vehicles, setVehicles] = React.useState([]);
  const [locations, setLocations] = React.useState([]);
  const [zones, setZones] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [locationFilter, setLocationFilter] = React.useState('');
  const [drawing, setDrawing] = React.useState(false);
  const [draft, setDraft] = React.useState([]);
  const [zoneTitle, setZoneTitle] = React.useState('');
  const [zoneLocationId, setZoneLocationId] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);
  const [state, setState] = React.useState('loading');
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const mapRef = React.useRef(null);
  const drawingRef = React.useRef(false);
  const focusedLocationRef = React.useRef('');

  React.useEffect(() => { drawingRef.current = drawing; }, [drawing]);

  const load = React.useCallback(async () => {
    if (!authorization) { return; }
    setState('loading');
    setError('');
    try {
      const [vehicleList, locationList, zoneList] = await Promise.all([
        getRentalVehicles(authorization),
        getRentalLocations(authorization),
        getRentalReturnZones(authorization),
      ]);
      setVehicles(vehicleList);
      setLocations(locationList);
      setZones(zoneList);
      // Пункт по умолчанию ставится через функцию: если держать zoneLocationId
      // в зависимостях загрузки, выбор пункта в форме зоны перезагружает карту.
      setZoneLocationId(current => current || (locationList[0] && locationList[0].id) || '');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setState('ready');
      setLoaded(true);
    }
  }, [authorization]);

  React.useEffect(() => { load(); }, [load]);

  const visibleVehicles = vehicles.filter(vehicle => {
    if (statusFilter && vehicle.status !== statusFilter) { return false; }
    if (locationFilter && vehicle.location_id !== locationFilter) { return false; }
    return true;
  });

  const visibleZones = zones.filter(zone => (
    !locationFilter || zone.location_id === locationFilter
  ));

  /**
   * Карта неуправляемая: `defaultState` читается один раз при монтировании,
   * дальше масштаб и центр принадлежат оператору. Раньше здесь стоял `state`,
   * и каждый рендер — в том числе клик по карте при рисовании зоны — возвращал
   * карту к исходному центру и зуму.
   */
  const initialCenter = React.useMemo(() => {
    const withCoordinates = vehicles.filter(vehicle => vehicle.coordinates);
    if (withCoordinates.length) {
      return [withCoordinates[0].coordinates.lat, withCoordinates[0].coordinates.lng];
    }
    if (locations.length) {
      return [locations[0].coordinates.lat, locations[0].coordinates.lng];
    }
    return DEFAULT_CENTER;
  }, [vehicles, locations]);

  const focusOn = React.useCallback((point, zoom) => {
    const map = mapRef.current;
    if (!map || !point || point.lat === null || point.lat === undefined) { return; }
    map.setCenter([Number(point.lat), Number(point.lng)], zoom || map.getZoom(), {duration: 250});
  }, []);

  // Единственный автоматический перевод карты — смена пункта в фильтре, и то
  // один раз на пункт: обновление списка не должно сбивать текущий вид.
  React.useEffect(() => {
    if (!locationFilter) {
      focusedLocationRef.current = '';
      return;
    }
    if (focusedLocationRef.current === locationFilter) { return; }
    const location = locations.find(item => item.id === locationFilter);
    if (!location) { return; }
    focusedLocationRef.current = locationFilter;
    focusOn(location.coordinates, FOCUS_ZOOM);
  }, [locationFilter, locations, focusOn]);

  const selectedZoneId = selected && selected.kind === 'zone' ? selected.id : null;
  const selectedVehicle = selected && selected.kind === 'vehicle'
    ? vehicles.find(item => item.id === selected.id)
    : null;
  const selectedLocation = selected && selected.kind === 'location'
    ? locations.find(item => item.id === selected.id)
    : null;
  const selectedZone = selectedZoneId
    ? zones.find(item => item.id === selectedZoneId)
    : null;

  const locationTitle = locationId => {
    const location = locations.find(item => item.id === locationId);
    return location ? `${location.city} · ${location.title}` : 'не привязан';
  };

  /**
   * Клик по метке или контуру всплывает до карты, поэтому выбор объекта гасит
   * событие: иначе при рисовании зоны один клик и выбирал бы объект, и ставил точку.
   */
  const selectObject = (kind, id) => event => {
    if (drawingRef.current) { return; }
    if (event && typeof event.stopPropagation === 'function') { event.stopPropagation(); }
    setSuccess('');
    setSelected({kind, id});
  };

  const handleMapClick = event => {
    if (!drawingRef.current) { return; }
    const coordinates = event.get('coords');
    setDraft(current => [...current, {lat: coordinates[0], lng: coordinates[1]}]);
  };

  const startDrawing = () => {
    setDrawing(true);
    setDraft([]);
    setSelected(null);
    setSuccess('');
    setError('');
  };

  const cancelDrawing = () => {
    setDrawing(false);
    setDraft([]);
  };

  const saveZone = async () => {
    if (draft.length < 3) {
      setError('Зона задаётся минимум тремя точками');
      return;
    }
    if (!zoneLocationId) {
      setError('Выберите пункт выдачи, к которому относится зона');
      return;
    }
    if (!zoneTitle.trim()) {
      setError('Укажите название зоны');
      return;
    }
    setState('saving');
    setError('');
    try {
      await createRentalReturnZone(authorization, {
        location_id: zoneLocationId,
        title: zoneTitle.trim(),
        polygon: draft,
        active: true,
      });
      setSuccess('Зона возврата сохранена');
      setZoneTitle('');
      cancelDrawing();
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setState('ready');
    }
  };

  const toggleZone = async zone => {
    setError('');
    try {
      await updateRentalReturnZone(authorization, zone.id, {active: !zone.active});
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  const removeZone = async zone => {
    setError('');
    try {
      await deleteRentalReturnZone(authorization, zone.id);
      setSelected(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  return (
    <>
      {!hasYandexMapsApiKey && (
        <State>
          Ключ Яндекс.Карт не задан. Карта работает в ограниченном режиме: добавьте
          <code> REACT_APP_YANDEX_MAPS_API_KEY </code>
          в <code>.env.local</code> панели.
        </State>
      )}

      <Toolbar>
        <Select value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
          <option value="">Все статусы</option>
          {Object.keys(statusLabels).map(status => (
            <option key={status} value={status}>{statusLabels[status]}</option>
          ))}
        </Select>
        <Select value={locationFilter} onChange={event => setLocationFilter(event.target.value)}>
          <option value="">Все пункты</option>
          {locations.map(location => (
            <option key={location.id} value={location.id}>
              {location.city} · {location.title}
            </option>
          ))}
        </Select>
        <ActionButton onClick={load}>Обновить</ActionButton>
      </Toolbar>

      {error && <ErrorText>{error}</ErrorText>}
      {success && <SuccessText>{success}</SuccessText>}

      <MapBox>
        {loaded ? (
          <YandexMapsProvider>
            <Map
              instanceRef={instance => { mapRef.current = instance; }}
              defaultState={{center: initialCenter, zoom: DEFAULT_ZOOM}}
              width="100%"
              height="100%"
              onClick={handleMapClick}
              options={{suppressMapOpenBlock: true}}
            >
              {locations.map(location => (
                <Placemark
                  key={location.id}
                  geometry={[location.coordinates.lat, location.coordinates.lng]}
                  properties={{iconCaption: `${location.city} · ${location.title}`}}
                  options={{
                    preset: location.id === (selectedLocation && selectedLocation.id)
                      ? 'islands#darkOrangeHomeCircleIcon'
                      : 'islands#blueHomeCircleIcon',
                    zIndex: location.id === (selectedLocation && selectedLocation.id) ? 800 : 600,
                  }}
                  onClick={selectObject('location', location.id)}
                />
              ))}

              {visibleVehicles
                .filter(vehicle => vehicle.coordinates)
                .map(vehicle => (
                  <Placemark
                    key={vehicle.id}
                    geometry={[vehicle.coordinates.lat, vehicle.coordinates.lng]}
                    properties={{iconCaption: vehicle.license_plate}}
                    options={{
                      preset: vehicle.id === (selectedVehicle && selectedVehicle.id)
                        ? 'islands#circleDotIcon'
                        : 'islands#circleIcon',
                      iconColor: statusColors[vehicle.status] || '#8b949f',
                      zIndex: vehicle.id === (selectedVehicle && selectedVehicle.id) ? 900 : 700,
                    }}
                    onClick={selectObject('vehicle', vehicle.id)}
                  />
                ))}

              {visibleZones.map(zone => (
                <Polygon
                  key={zone.id}
                  geometry={[zone.polygon.map(point => [point.lat, point.lng])]}
                  options={{
                    fillColor: zone.active ? '#21c17a33' : '#8b949f33',
                    strokeColor: zone.active ? '#21c17a' : '#8b949f',
                    strokeWidth: zone.id === selectedZoneId ? 4 : 2,
                  }}
                  onClick={selectObject('zone', zone.id)}
                />
              ))}

              {draft.length > 0 && (
                <>
                  {draft.length >= 3 && (
                    <Polygon
                      geometry={[draft.map(point => [point.lat, point.lng])]}
                      options={{
                        fillColor: '#1a9ee833',
                        strokeColor: '#1a9ee8',
                        strokeWidth: 2,
                        strokeStyle: 'dash',
                      }}
                    />
                  )}
                  {draft.map((point, index) => (
                    <Placemark
                      key={`draft-${index}`}
                      geometry={[point.lat, point.lng]}
                      properties={{iconContent: String(index + 1)}}
                      options={{preset: 'islands#blueStretchyIcon'}}
                    />
                  ))}
                </>
              )}
            </Map>
          </YandexMapsProvider>
        ) : (
          <MapPlaceholder>Загрузка карты…</MapPlaceholder>
        )}
      </MapBox>

      <Legend>
        {Object.keys(statusLabels).map(status => (
          <LegendItem key={status}>
            <LegendDot color={statusColors[status]} />
            {statusLabels[status]}
          </LegendItem>
        ))}
      </Legend>

      {selectedVehicle && (
        <Card>
          <SelectionHead>
            <CardTitle>{selectedVehicle.brand} {selectedVehicle.model}</CardTitle>
            <ActionButton type="button" onClick={() => setSelected(null)}>Снять выделение</ActionButton>
          </SelectionHead>
          <DefinitionList>
            <dt>Госномер</dt>
            <dd>{selectedVehicle.license_plate || '—'}</dd>
            <dt>Состояние</dt>
            <dd>
              <Badge
                danger={selectedVehicle.status === 'maintenance'}
                success={selectedVehicle.status === 'available'}
              >
                {statusLabels[selectedVehicle.status] || selectedVehicle.status}
              </Badge>
              {selectedVehicle.hidden ? ' · скрыт от клиентов' : ''}
            </dd>
            <dt>Пункт выдачи</dt>
            <dd>{locationTitle(selectedVehicle.location_id)}</dd>
            <dt>Цена</dt>
            <dd>{money(selectedVehicle.daily_rate)}</dd>
            <dt>Координаты</dt>
            <dd>{formatPoint(selectedVehicle.coordinates)}</dd>
          </DefinitionList>
          <Actions>
            <ActionButton type="button" onClick={() => focusOn(selectedVehicle.coordinates, 16)}>
              Показать на карте
            </ActionButton>
          </Actions>
        </Card>
      )}

      {selectedLocation && (
        <Card>
          <SelectionHead>
            <CardTitle>{selectedLocation.city} · {selectedLocation.title}</CardTitle>
            <ActionButton type="button" onClick={() => setSelected(null)}>Снять выделение</ActionButton>
          </SelectionHead>
          <DefinitionList>
            <dt>Адрес</dt>
            <dd>{selectedLocation.address || '—'}</dd>
            <dt>Часы работы</dt>
            <dd>{selectedLocation.opens_at} — {selectedLocation.closes_at}</dd>
            <dt>Телефон</dt>
            <dd>{selectedLocation.phone || '—'}</dd>
            <dt>Автомобили</dt>
            <dd>
              {selectedLocation.vehicles_available} свободно из {selectedLocation.vehicles_total}
              {selectedLocation.hidden ? ' · пункт скрыт от клиентов' : ''}
            </dd>
            <dt>Координаты</dt>
            <dd>{formatPoint(selectedLocation.coordinates)}</dd>
          </DefinitionList>
          <Actions>
            {locationFilter === selectedLocation.id ? (
              <ActionButton type="button" onClick={() => setLocationFilter('')}>
                Показать все пункты
              </ActionButton>
            ) : (
              <ActionButton type="button" onClick={() => setLocationFilter(selectedLocation.id)}>
                Только этот пункт
              </ActionButton>
            )}
            <ActionButton type="button" onClick={() => focusOn(selectedLocation.coordinates, FOCUS_ZOOM)}>
              Показать на карте
            </ActionButton>
          </Actions>
        </Card>
      )}

      {selectedZone && (
        <Card>
          <SelectionHead>
            <CardTitle>{selectedZone.title}</CardTitle>
            <ActionButton type="button" onClick={() => setSelected(null)}>Снять выделение</ActionButton>
          </SelectionHead>
          <DefinitionList>
            <dt>Пункт выдачи</dt>
            <dd>{locationTitle(selectedZone.location_id)}</dd>
            <dt>Состояние</dt>
            <dd><Badge success={selectedZone.active}>{selectedZone.active ? 'Активна' : 'Выключена'}</Badge></dd>
            <dt>Точек контура</dt>
            <dd>{selectedZone.polygon.length}</dd>
          </DefinitionList>
          <Actions>
            <ActionButton type="button" onClick={() => toggleZone(selectedZone)}>
              {selectedZone.active ? 'Выключить' : 'Включить'}
            </ActionButton>
            <ActionButton type="button" onClick={() => focusOn(zoneCenter(selectedZone), FOCUS_ZOOM)}>
              Показать на карте
            </ActionButton>
            <ActionButton type="button" danger onClick={() => removeZone(selectedZone)}>
              Удалить
            </ActionButton>
          </Actions>
        </Card>
      )}

      {!selected && !drawing && (
        <Hint>Кликните по автомобилю, пункту выдачи или зоне на карте — покажем детали и действия.</Hint>
      )}

      <ZonesCard>
        <CardTitle>Зоны возврата</CardTitle>
        {drawing ? (
          <>
            <State>
              Кликайте по карте, чтобы поставить точки контура. Нужно минимум три точки,
              поставлено: {draft.length}.
            </State>
            <DrawRow>
              <Select value={zoneLocationId} onChange={event => setZoneLocationId(event.target.value)}>
                <option value="">Пункт выдачи</option>
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.city} · {location.title}
                  </option>
                ))}
              </Select>
              <Input
                placeholder="Название зоны"
                value={zoneTitle}
                onChange={event => setZoneTitle(event.target.value)}
              />
            </DrawRow>
            <Actions>
              <ActionButton primary disabled={state === 'saving'} onClick={saveZone}>
                Сохранить зону
              </ActionButton>
              <ActionButton onClick={() => setDraft(current => current.slice(0, -1))}>
                Убрать последнюю точку
              </ActionButton>
              <ActionButton onClick={cancelDrawing}>Отменить</ActionButton>
            </Actions>
          </>
        ) : (
          <>
            {visibleZones.length === 0 ? (
              <State>
                Зон пока нет. Зона показывается клиенту при возврате и определяет, где
                разрешено оставить автомобиль.
              </State>
            ) : (
              <List>
                {visibleZones.map(zone => (
                  <ListButton
                    key={zone.id}
                    active={zone.id === selectedZoneId}
                    onClick={() => {
                      setSelected({kind: 'zone', id: zone.id});
                      focusOn(zoneCenter(zone), FOCUS_ZOOM);
                    }}
                  >
                    <div>
                      <strong>{zone.title}</strong>
                      <div>{zone.polygon.length} точек</div>
                    </div>
                    <Badge>{zone.active ? 'Активна' : 'Выключена'}</Badge>
                  </ListButton>
                ))}
              </List>
            )}
            <Actions>
              <ActionButton primary onClick={startDrawing}>Нарисовать зону</ActionButton>
            </Actions>
          </>
        )}
      </ZonesCard>
    </>
  );
}

/**
 * Обёртка с Layout: props передаются дочернему элементу через cloneElement,
 * поэтому содержимое вынесено в отдельный компонент.
 */
function FleetMap() {
  return (
    <Layout
      title="Карта автомобилей"
      description="Все автомобили автопарка со статусами и зоны, в которых разрешено оставлять машину при возврате. Клик по метке или контуру открывает карточку объекта."
    >
      <FleetMapContent />
    </Layout>
  );
}

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`;

const MapBox = styled.div`
  height: 520px;
  border: 1px solid #cad0d9;
  border-radius: 10px;
  overflow: hidden;
`;

const MapPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #818286;
  background: #f5f7f9;
  font-size: 14px;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 12px 0 20px;
  font-size: 13px;
  color: #6b7480;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const SelectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  h2 { margin-bottom: 0; }
  margin-bottom: 16px;
`;

const Hint = styled.div`
  margin-bottom: 20px;
  color: #818286;
  font-size: 13px;
`;

const ZonesCard = styled(Card)`
  margin-top: 20px;
`;

const DrawRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 12px 0;
`;

export default FleetMap;
